import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";

import type { components } from "@/shared/api";
import { cn } from "@/shared/lib";

import { formatStat } from "./formatStat";
import { statTone } from "./statTone";

type Section = components["schemas"]["TooltipSectionOut"];

const TITLE = {
  innate: null,
  // Заголовок секции эффекта («Пассивно», «Активно»), не путать с плашкой «Активный».
  passive: msg({ message: "Passive", context: "item effect section" }),
  active: msg({ message: "Active", context: "item effect section" }),
} as const;

const TONE = {
  plus: cn("text-good"),
  minus: cn("text-bad"),
  plain: cn("text-foreground"),
} as const;

type TooltipSectionProps = {
  section: Section;
  // У умения секция одна: заголовок «Активно» над ней лишний.
  hideTitle?: boolean;
};

// Секция описания предмета: заголовок эффекта, текст игры и числа. Плюс — зелёным, минус —
// красным; главное число секции (в игре — крупно) — жирнее.
export const TooltipSection = ({ section, hideTitle = false }: TooltipSectionProps) => {
  const { i18n } = useLingui();
  const title = hideTitle ? null : TITLE[section.kind];
  return (
    <div className="flex flex-col gap-1.5 [&+&]:border-t [&+&]:border-border [&+&]:pt-2.5">
      {title !== null && (
        <h4 className="text-xs font-semibold tracking-wider text-subtle uppercase">
          {i18n._(title)}
        </h4>
      )}
      {section.text !== "" && (
        <p className="text-sm leading-snug whitespace-pre-line text-foreground-soft">
          {section.text}
        </p>
      )}
      {section.stats.length > 0 && (
        <dl className="flex flex-col gap-1 text-[13px]">
          {section.stats.map((stat, index) => (
            <div key={`${stat.label}-${index}`} className="flex justify-between gap-3">
              <dt className="text-muted">{stat.label}</dt>
              <dd
                className={cn(
                  "font-mono whitespace-nowrap",
                  TONE[statTone(stat)],
                  stat.important ? "font-bold" : "font-medium",
                )}
              >
                {formatStat(stat, i18n.locale)}
              </dd>
            </div>
          ))}
        </dl>
      )}
    </div>
  );
};
